import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Autocomplete,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Box,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import TagsField from "../components/TagsField";
import BundlePublishing from "../components/BundlePublishing";
import RichTextEditor from "../components/RichTextEditor";
import BundleMetafieldsSection from "../sections/product/BundleMetafieldsSection";
import ImageThumb from "../components/ImageThumb";
import useBundleableProducts from "../hooks/useBundleableProducts";
import { TYPE_OPTIONS } from "./BundleCreator";

const STATUS_OPTIONS = ["ACTIVE", "DRAFT", "ARCHIVED"];

const formatPrice = (value) =>
  `$ ${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const paperSx = {
  width: "100%",
  backgroundColor: "background.paper",
  mb: 2,
  p: 3,
};

export default function BundleEditor({ id, onClose = () => {} }) {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [tags, setTags] = useState([]);
  const [publications, setPublications] = useState([]);
  const [discounts, setDiscounts] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [variantImageFiles, setVariantImageFiles] = useState({});
  const [variantImagePreviews, setVariantImagePreviews] = useState({});
  const [removeImage, setRemoveImage] = useState(false);
  const [removeVariantImages, setRemoveVariantImages] = useState([]);
  const [mfValues, setMfValues] = useState({ metafields: {} });

  const [editableComponents, setEditableComponents] = useState([]);
  const [compSearch, setCompSearch] = useState("");
  const [editableSizes, setEditableSizes] = useState([]);
  const [canEditComponents, setCanEditComponents] = useState(true);
  const tempIdRef = useRef(0);
  const nextTempId = () => `new-${(tempIdRef.current += 1)}`;

  const { options: compOptions, loading: compLoading } = useBundleableProducts(compSearch);

  const loadBundle = async () => {
    try {
      setLoading(true);
      const response = await fetchWithInterceptor(`/api/bundles/${id}`, {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      });
      if (!response.ok) throw new Error("Failed to fetch bundle");
      const data = await response.json();
      setBundle(data);
      setCanEditComponents(data.canEditComponents !== false);
      setTitle(data.title || "");
      setDescription(data.description || "");
      setStatus(data.status || "ACTIVE");
      setTags((data.tags || []).join(", "));
      setMfValues({ metafields: data.metafields || {} });
      setPublications(data.publications || []);
      setImagePreview(data.image || null);

      const variantPreviews = {};
      (data.variants || []).forEach((v) => {
        variantPreviews[v.variantId] = v.image || null;
      });
      setVariantImagePreviews(variantPreviews);

      const initialDiscounts = {};
      (data.variants || []).forEach((v) => {
        initialDiscounts[v.variantId] = v.discount ?? "";
      });
      setDiscounts(initialDiscounts);

      const isPack = (data.variants || []).length > 1;
      if (isPack) {
        setEditableSizes(
          (data.variants || []).map((v) => ({
            variantId: v.variantId,
            quantity: v.components?.[0]?.quantity ?? "",
            imageFile: null,
            imagePreview: v.image || null,
          }))
        );
      } else {
        setEditableComponents(data.variants?.[0]?.components || []);
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading bundle", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBundle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
  };

  const handleVariantImageChange = (variantId, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const prevPreview = variantImagePreviews[variantId];
    if (prevPreview?.startsWith("blob:")) URL.revokeObjectURL(prevPreview);
    setVariantImageFiles((prev) => ({ ...prev, [variantId]: file }));
    setVariantImagePreviews((prev) => ({
      ...prev,
      [variantId]: URL.createObjectURL(file),
    }));
    setRemoveVariantImages((prev) => prev.filter((vid) => vid !== variantId));
  };

  const handleRemoveVariantImage = (variantId) => {
    const prevPreview = variantImagePreviews[variantId];
    if (prevPreview?.startsWith("blob:")) URL.revokeObjectURL(prevPreview);
    setVariantImageFiles((prev) => {
      const next = { ...prev };
      delete next[variantId];
      return next;
    });
    setVariantImagePreviews((prev) => ({ ...prev, [variantId]: null }));
    setRemoveVariantImages((prev) =>
      prev.includes(variantId) ? prev : [...prev, variantId]
    );
  };

  const handleDiscountChange = (variantId, value) => {
    setDiscounts((prev) => ({ ...prev, [variantId]: value }));
  };

  const handleRemoveComponent = (idx) => {
    setEditableComponents((prev) => prev.filter((component, index) => index !== idx));
  };

  const handleRemoveSize = (idx) => {
    const removed = editableSizes[idx];
    const preview = removed?.variantId
      ? variantImagePreviews[removed.variantId]
      : removed?.imagePreview;
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);

    if (removed?.variantId) {
      setVariantImageFiles((prev) => {
        const next = { ...prev };
        delete next[removed.variantId];
        return next;
      });
      setVariantImagePreviews((prev) => {
        const next = { ...prev };
        delete next[removed.variantId];
        return next;
      });
      setRemoveVariantImages((prev) => prev.filter((vid) => vid !== removed.variantId));
    }

    setEditableSizes((prev) => prev.filter((size, index) => index !== idx));
  };

  const handleNewSizeImageChange = (idx, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const prevPreview = editableSizes[idx]?.imagePreview;
    if (prevPreview?.startsWith("blob:")) URL.revokeObjectURL(prevPreview);
    setEditableSizes((prev) =>
      prev.map((size, index) =>
        index === idx ? { ...size, imageFile: file, imagePreview: URL.createObjectURL(file) } : size
      )
    );
  };

  const handleSave = async () => {
    const isPack = (bundle.variants || []).length > 1;

    const hasDiscount = (value) =>
      value !== undefined && value !== null && String(value).trim() !== "";

    if (isPack) {
      const sizes = editableSizes.filter((s) => Number(s.quantity) > 0);
      const quantities = sizes.map((s) => Number(s.quantity));

      if (quantities.length < 2) {
        appContext.showMessage(
          "A pack needs at least two presentations (e.g. 12-Pack and 24-Pack)",
          "warning"
        );
        return;
      }
      if (new Set(quantities).size !== quantities.length) {
        appContext.showMessage(
          "Each presentation must have a different quantity",
          "warning"
        );
        return;
      }
      if (!sizes.every((s) => hasDiscount(discounts[s.variantId || s.tempId]))) {
        appContext.showMessage("Set a discount for every presentation", "warning");
        return;
      }
    } else if (!(bundle.variants || []).every((v) => hasDiscount(discounts[v.variantId]))) {
      appContext.showMessage("Set the discount percentage", "warning");
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("status", status);
      const tagsArray = Array.isArray(tags)
        ? tags
        : String(tags)
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
      formData.append("tags", JSON.stringify(tagsArray));
      formData.append("publications", JSON.stringify(publications));

      const discountsPayload = isPack
        ? editableSizes
            .filter((s) => Number(s.quantity) > 0)
            .map((s) => ({
              match: `${Number(s.quantity)} Pack`,
              percentage: Number(discounts[s.variantId || s.tempId]) || 0,
            }))
        : (bundle.variants || []).map((v) => ({
            variantId: v.variantId,
            percentage: Number(discounts[v.variantId]) || 0,
          }));
      formData.append("discounts", JSON.stringify(discountsPayload));

      if (imageFile) formData.append("image", imageFile);
      if (removeImage) formData.append("removeImage", "true");

      if (isPack) {
        formData.append("bundleType", "PACK");
        formData.append(
          "packSizes",
          JSON.stringify(
            editableSizes
              .filter((s) => Number(s.quantity) > 0)
              .map((s) => ({ quantity: Number(s.quantity) }))
          )
        );
        const removedImageQuantities = [];
        editableSizes.forEach((s) => {
          const qty = Number(s.quantity);
          if (!qty) return;
          const newFile = s.variantId ? variantImageFiles[s.variantId] : s.imageFile;
          if (newFile) {
            formData.append(`packImage_${qty}`, newFile);
          } else if (s.variantId && removeVariantImages.includes(s.variantId)) {
            removedImageQuantities.push(qty);
          }
        });
        formData.append("removedImageQuantities", JSON.stringify(removedImageQuantities));
      } else {
        formData.append("bundleType", "FIXED");
        formData.append("fixedBundleVariantId", bundle.variants?.[0]?.variantId || "");
        formData.append(
          "fixedComponents",
          JSON.stringify(
            editableComponents
              .filter((c) => c.variantId)
              .map((c) => ({ variantId: c.variantId, quantity: Math.max(1, Number(c.quantity) || 1) }))
          )
        );
        formData.append("metafields", JSON.stringify(mfValues.metafields || {}));
      }

      const response = await fetchWithInterceptor(`/api/bundles/${id}`, {
        method: "PUT",
        headers: {
          "x-access-token": authContext.getToken(),
        },
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to save bundle");
      }
      appContext.showMessage("Bundle updated successfully", "success");
      onClose();
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error saving bundle", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!bundle) {
    return (
      <Container sx={{ mt: 3 }} maxWidth="100%">
        <Button startIcon={<ArrowBackIcon />} onClick={() => onClose()}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Bundle not found.</Typography>
      </Container>
    );
  }

  const variants = bundle.variants || [];
  const isPack = variants.length > 1;
  const typeValue = isPack ? "PACK" : "FIXED";
  const bundleType =
    TYPE_OPTIONS.find((t) => t.value === typeValue)?.label ?? typeValue;

  const baseComponent = variants[0]?.components?.[0] || null;

  // One discount field per row: labelled presentations for PACK, the lone variant for FIXED.
  const discountRows = isPack
    ? editableSizes.map((s) => ({
        key: s.variantId || s.tempId,
        label: s.quantity ? `${s.quantity} Pack` : "New presentation",
      }))
    : variants.map((v) => ({ key: v.variantId, label: null }));

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", ml: 2, mt: 3, mb: 1 }}>
        <IconButton onClick={() => onClose()} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Edit bundle details, status, discount, components and images.
        </Typography>
      </Box>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        {!canEditComponents && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This bundle was created with Shopify's Bundles app. The disabled fields can
            only be edited from the Shopify admin.
          </Alert>
        )}

        {/* Details */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Bundle Details
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Type"
                fullWidth
                value={bundleType}
                disabled
                helperText="Set at creation, can't be changed"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TagsField
                value={tags}
                isEditingMode={true}
                disablePadding={true}
                onChange={(value) => setTags(value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Products */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Products
          </Typography>

          {isPack ? (
            /* ---- Single Product (PACK): base product + editable presentations ---- */
            <>
              {baseComponent && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <ImageThumb
                    src={baseComponent.image}
                    alt={baseComponent.title}
                    size={48}
                    zoomable
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {baseComponent.title}
                  </Typography>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Presentations (each one becomes a variant)
              </Typography>

              {editableSizes.map((s, idx) => {
                const name = s.quantity ? `${s.quantity} Pack` : "";
                const imgPreview = s.variantId
                  ? variantImagePreviews[s.variantId]
                  : s.imagePreview;
                return (
                  <Box
                    key={s.variantId || s.tempId}
                    sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                  >
                    <TextField
                      label="Units"
                      size="small"
                      type="number"
                      value={s.quantity}
                      onChange={(e) =>
                        setEditableSizes((prev) =>
                          prev.map((size, index) =>
                            index === idx ? { ...size, quantity: e.target.value } : size
                          )
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                      disabled={!canEditComponents}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Name"
                      size="small"
                      value={name}
                      disabled
                      sx={{ width: 160 }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <ImageThumb src={imgPreview} alt={name} size={40} zoomable />
                      <Button component="label" size="small" sx={{ minWidth: 0 }}>
                        {imgPreview ? "Change image" : "Upload image"}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) => {
                            if (s.variantId) {
                              handleVariantImageChange(s.variantId, e);
                            } else {
                              handleNewSizeImageChange(idx, e);
                            }
                          }}
                        />
                      </Button>
                      {imgPreview && (
                        <Button
                          size="small"
                          sx={{ minWidth: 0 }}
                          onClick={() => {
                            if (s.variantId) {
                              handleRemoveVariantImage(s.variantId);
                            } else {
                              if (s.imagePreview?.startsWith("blob:")) {
                                URL.revokeObjectURL(s.imagePreview);
                              }
                              setEditableSizes((prev) =>
                                prev.map((size, index) =>
                                  index === idx
                                    ? { ...size, imageFile: null, imagePreview: null }
                                    : size
                                )
                              );
                            }
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: "auto", minWidth: 100, textAlign: "right" }}
                    >
                      {s.variantId
                        ? formatPrice(variants.find((v) => v.variantId === s.variantId)?.price)
                        : "—"}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={
                        !canEditComponents ||
                        (Number(s.quantity) > 0 &&
                          editableSizes.filter((size) => Number(size.quantity) > 0).length <= 2)
                      }
                      onClick={() => handleRemoveSize(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}

              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={!canEditComponents}
                onClick={() =>
                  setEditableSizes((prev) => [
                    ...prev,
                    { variantId: null, tempId: nextTempId(), quantity: "", imageFile: null, imagePreview: null },
                  ])
                }
                sx={{ mt: 1 }}
              >
                Add presentation
              </Button>
            </>
          ) : (
            /* ---- Multiple Products (FIXED): editable component list ---- */
            <>
              <Autocomplete
                disabled={!canEditComponents}
                options={compOptions.filter(
                  (o) => !editableComponents.some((c) => c.productId === o.id)
                )}
                loading={compLoading}
                filterOptions={(x) => x}
                getOptionLabel={(o) => o.title || ""}
                value={null}
                blurOnSelect
                onChange={(_, product) => {
                  if (!product) return;
                  if (!product.defaultVariantId) {
                    appContext.showMessage("That product has no variant to add", "warning");
                    return;
                  }
                  setEditableComponents((prev) => [
                    ...prev,
                    {
                      variantId: product.defaultVariantId,
                      productId: product.id,
                      title: product.title,
                      image: product.image,
                      price: null,
                      quantity: 1,
                    },
                  ]);
                }}
                onInputChange={(_, value) => setCompSearch(value)}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
                    {...props}
                  >
                    <ImageThumb src={option.image} alt={option.title} size={40} />
                    <Typography variant="body2">{option.title}</Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add product"
                    placeholder="Search products…"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {editableComponents.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No products added yet.
                </Typography>
              )}

              {editableComponents.map((c, idx) => (
                <Box key={c.variantId || `comp-${idx}`}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
                  >
                    <ImageThumb src={c.image} alt={c.title} size={40} zoomable />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {c.title}
                    </Typography>
                    <TextField
                      label="Qty"
                      type="number"
                      size="small"
                      value={c.quantity}
                      onChange={(e) =>
                        setEditableComponents((prev) =>
                          prev.map((component, index) =>
                            index === idx ? { ...component, quantity: e.target.value } : component
                          )
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                      disabled={!canEditComponents}
                      sx={{ width: 100 }}
                    />
                    <IconButton
                      size="small"
                      disabled={!canEditComponents}
                      onClick={() => handleRemoveComponent(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {idx < editableComponents.length - 1 && <Divider />}
                </Box>
              ))}
            </>
          )}
        </Paper>

        {/* Discount per variant */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Discount
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the discount percentage for the bundle. We will add up the unit
            prices of the products included in the bundle and apply the discount
            percentage you specify here. For example, if the total price of the
            products in the bundle is $100.00 and you apply a 10% discount, the
            bundle price will be $90.00.
          </Typography>
          {discountRows.map((row, idx) => (
            <Box key={row.key}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 2,
                  py: 1,
                }}
              >
                {row.label && (
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {row.label}
                  </Typography>
                )}
                <TextField
                  label="Discount %"
                  type="number"
                  value={discounts[row.key] ?? ""}
                  onChange={(e) => handleDiscountChange(row.key, e.target.value)}
                  inputProps={{ min: 0, max: 100 }}
                  sx={{ width: 320 }}
                />
              </Box>
              {idx < discountRows.length - 1 && <Divider />}
            </Box>
          ))}
        </Paper>

        {/* Metafields — FIXED only */}
        {!isPack && (
          <Paper sx={paperSx}>
            <BundleMetafieldsSection values={mfValues} setValues={setMfValues} />
          </Paper>
        )}

        {/* Publishing */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Publishing
          </Typography>
          <BundlePublishing
            status={status}
            publications={publications}
            onChange={setPublications}
          />
        </Paper>

        {/* Description (+ image for FIXED) */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {isPack ? "Description" : "Description & Media"}
          </Typography>

          <Typography variant="body2" gutterBottom>
            Description
          </Typography>
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.23)",
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <RichTextEditor value={description} onChange={setDescription} />
          </Box>

          {!isPack && (
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                bgcolor: "#f5f5f5",
                height: 300,
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {imagePreview && (
                <Box
                  sx={{
                    border: "1px solid #aaa",
                    borderRadius: 2,
                    overflow: "hidden",
                    width: 260,
                    bgcolor: "#fff",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt={title}
                    style={{ width: "160px", height: "auto", display: "block", borderRadius: "8px" }}
                  />
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" component="label">
                  {imagePreview ? "Change image" : "Upload image"}
                  <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                </Button>
                {imagePreview && (
                  <Button variant="outlined" onClick={handleRemoveImage}>
                    Remove image
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
          <Button onClick={() => onClose()} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </Box>
      </Container>
    </>
  );
}
